import { handleActions, createAction } from "redux-actions";
import { api, notification } from "../../services";
import { Chat, rootReducerInterface } from "../../typings/interfaces";
import {
  chatInterface,
  sendMessageRequest,
  getChatRequest,
  getChatsRequest,
} from "../../typings/interfaces";
import { CHAT_ITEM_PER_PAGE } from "../../containers/ChatsPage/constants/constants";

const CHATS_GET = "CHATS_GET";

const CHAT_GET = "CHAT_GET";
const CHAT_SET = "CHAT_SET";
const CHAT_CLEAR = "CHAT_CLEAR";

const MESSAGE_SEND = "MESSAGE_SEND";
const MESSAGE_SEND_BATCH = "MESSAGE_SEND_BATCH";

const MESSAGE_MARK_AS_READ = "MESSAGE_MARK_AS_READ";
const MESSAGE_UNREAD_COUNT_GET = "MESSAGE_UNREAD_COUNT_GET";

const USER_BLOCK = "USER_BLOCK";
const USER_UNBLOCK = "USER_UNBLOCK";

const CHAT_DELETE = "CHAT_DELETE";

const FILTER_INFO_GET = "FILTER_INFO_GET";
const USER_REPORT = "USER_REPORT";


// Actions

// Chats
export const getChats = createAction(
  CHATS_GET,
  async (params: getChatsRequest, callback?: Function) => {
    try {
      const response = await api.Chat.getChats({ ...params, itemPerPage: CHAT_ITEM_PER_PAGE });
      callback && callback(response);
      return response;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);

// Chat
export const getChat = createAction(
  CHAT_GET,
  async (params: getChatRequest, callback?: Function) => {
    try {
      const response = await api.Chat.getChat(params);
      callback && callback(response);
      return response;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);
export const setChat = createAction(CHAT_SET, (c: Chat | null) => c);
export const clearChat = createAction(CHAT_CLEAR);

// Message
export const sendMessage = createAction(
  MESSAGE_SEND,
  async (messageData: sendMessageRequest, callback?: Function) => {
    try {
      const response = await api.Chat.sendMessage(messageData);
      callback && callback(response);
      return response;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);
export const sendBatchMessages = createAction(
  MESSAGE_SEND_BATCH,
  async (messagesData: sendMessageRequest[], callback?: Function) => {
    try {
      let lastResponse = null;

      // Send all messages sequentially
      for (const messageData of messagesData) {
        lastResponse = await api.Chat.sendMessage(messageData);
      }

      // Return the final chat state (which includes all messages)
      callback && callback(lastResponse);
      return lastResponse;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);
export const markMessageAsRead = createAction(
  MESSAGE_MARK_AS_READ,
  async (chatId: string, messageId: string, callback?: Function) => {
    try {
      await api.Chat.markAsRead(chatId, messageId);
      callback && callback(true);
      return { chatId, messageId };
    } catch (error) {
      callback && callback(false, error);
      throw error;
    }
  }
);
export const getUnreadCount = createAction(
  MESSAGE_UNREAD_COUNT_GET,
  async (callback?: Function) => {
    try {
      const response = await api.Chat.getUnreadCount();
      callback && callback(response);
      return response;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);

export const reportUser = createAction(
  USER_REPORT,
  async (data: any, callback: Function) => {
    try {
      await api.Chat.reportUser(data);

      notification.show("Prijava je uspješna");
      callback && callback(true);

      return null;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

// Block/Unblock User
export const blockUser = createAction(
  USER_BLOCK,
  async (userId: string, callback?: Function) => {
    try {
      await api.Chat.blockUser(userId);
      callback && callback(true);
      return { userId };
    } catch (error) {
      callback && callback(false, error);
      throw error;
    }
  }
);

export const unblockUser = createAction(
  USER_UNBLOCK,
  async (userId: string, callback?: Function) => {
    try {
      await api.Chat.unblockUser(userId);
      callback && callback(true);
      return { userId };
    } catch (error) {
      callback && callback(false, error);
      throw error;
    }
  }
);

// Delete Chat
export const deleteChat = createAction(
  CHAT_DELETE,
  async (chatIds: string[], callback?: Function) => {
    try {
      await api.Chat.deleteChat(chatIds);
      callback && callback(true);
      return { chatIds };
    } catch (error) {
      callback && callback(false, error);
      throw error;
    }
  }
);


// Chats
export const getFilterInfo = createAction(
  FILTER_INFO_GET,
  async (callback?: Function) => {
    try {
      const response = await api.Chat.getFilterInfo();
      callback && callback(response);
      return response;
    } catch (error) {
      callback && callback(null, error);
      throw error;
    }
  }
);


// Selectors
export const selectChats = (state: rootReducerInterface) => state.chat.chats;
export const selectChat = (state: rootReducerInterface) => state.chat.chat;
export const selectUnreadCount = (state: rootReducerInterface) => state.chat.unreadCount;
export const selectChatPagination = (state: rootReducerInterface) => state.chat.pagination;

// Additional selectors based on BE response structure
export const selectChatParticipants = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  if (!chat) return null;

  return {
    senderId: chat.senderId,
    senderName: chat.senderName,
    recipientId: chat.recipientId,
    recipientName: chat.recipientName,
    isSenderBlocked: chat.isSenderBlocked,
    isRecipientBlocked: chat.isRecipientBlocked
  };
};

export const selectFilterInfo = (state: rootReducerInterface) => state.chat.filterInfo

export const selectLastReadMessageFromOtherUserIndex = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  const userId = state.auth.user.userId
  if (!chat || !chat.messages) return null;

  const lastReadMessageKey = userId === chat.senderId ? "recipientLastReadMessageId" : "senderLastReadMessageId";
  const lastReadMessageId = chat[lastReadMessageKey];

  return chat.messages.findIndex(message => message.messageId === lastReadMessageId)

}

export const selectFirstUnreadMessageIndex = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  const userId = state.auth.user.userId;
  if (!chat || !chat.messages || chat.messages.length === 0) return -1;

  const key = userId === chat.senderId ? "senderLastReadMessageId" : "recipientLastReadMessageId";
  const lastReadMessageId = chat[key];

  if (!lastReadMessageId) {
    // Never marked as read - first message from the other person is the first unread
    const otherUserId = userId === chat.senderId ? chat.recipientId : chat.senderId;
    return chat.messages.findIndex(m => m.senderId === otherUserId);
  }

  const lastReadIndex = chat.messages.findIndex(m => m.messageId === lastReadMessageId);
  if (lastReadIndex === -1 || lastReadIndex >= chat.messages.length - 1) return -1;

  return lastReadIndex + 1;
};

export const selectOtherUsersRegistrationDate = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  const userId = state.auth.user.userId
  if (!chat || !chat.messages) return null;

  const registrationDateKey = userId === chat.senderId ? "recipientRegistrationDate" : "senderRegistrationDate";
  return chat[registrationDateKey];
}

export const selectOtherUsersActiveAdsCount = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  const userId = state.auth.user.userId
  if (!chat || !chat.messages) return null;

  const activeAdsKey = userId === chat.senderId ? "recipientActiveAdsCount" : "senderActiveAdsCount";
  return chat[activeAdsKey];
}

export const selectOtherUsersReplyStatisticsMessage = (state: rootReducerInterface) => {
  const chat = state.chat.chat;
  if (!chat) return null;

  return chat.replyStatisticsMessage;
}

// Reducer
const initialState: chatInterface = {
  chat: null,
  chats: [],
  filterInfo: null,
  unreadCount: 0,
  pagination: null,
};

export default handleActions(
  {
    [CHATS_GET]: (state: chatInterface, { payload }) => ({
      ...state,
      chats: payload && payload.data ? payload.data : [],
      pagination: {
        currentPage: payload && payload.currentPage ? payload.currentPage : 1,
        count: payload && payload.count ? payload.count : 0,
      },
    }),

    [CHAT_GET]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: payload.messages ? payload : null,
    }),
    [CHAT_SET]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: payload
    }),
    [CHAT_CLEAR]: (state: chatInterface) => ({
      ...state,
      chat: null
    }),

    [MESSAGE_SEND]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: payload.messages ? {
        ...payload,
        replyStatisticsMessage: payload.replyStatisticsMessage || (state.chat && state.chat.replyStatisticsMessage)
      } : state.chat,
      chats: state.chats.map(chat =>
        chat.chatId === (payload && payload.chatId ? payload.chatId : null)
          ?
          payload
          : chat
      )
    }),
    [MESSAGE_SEND_BATCH]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: payload.messages ? {
        ...payload,
        replyStatisticsMessage: payload.replyStatisticsMessage || (state.chat && state.chat.replyStatisticsMessage)
      } : state.chat,
      chats: state.chats.map(chat =>
        chat.chatId === (payload && payload.chatId ? payload.chatId : null)
          ?
          payload
          : chat
      )
    }),
    [MESSAGE_MARK_AS_READ]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: state.chat ? {
        ...state.chat,
        senderLastReadMessageId: payload.messageId,
        recipientLastReadMessageId: payload.messageId
      } : state.chat,
      chats: state.chats.map(chat =>
        chat.chatId === payload.chatId
          ? { ...chat, unreadMessagesCount: Math.max(0, chat.unreadMessagesCount - 1) }
          : chat
      )
    }),
    [MESSAGE_UNREAD_COUNT_GET]: (state: chatInterface, { payload }) => ({
      ...state,
      unreadCount: payload && payload.count ? payload.count : 0
    }),

    [USER_BLOCK]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: state.chat && (state.chat.senderId === payload.userId || state.chat.recipientId === payload.userId)
        ? {
          ...state.chat,
          isSenderBlocked: state.chat.senderId === payload.userId ? true : state.chat.isSenderBlocked,
          isRecipientBlocked: state.chat.recipientId === payload.userId ? true : state.chat.isRecipientBlocked
        }
        : state.chat,
      chats: state.chats.map(chat =>
        chat.senderId === payload.userId || chat.recipientId === payload.userId
          ? {
            ...chat,
            isSenderBlocked: chat.senderId === payload.userId ? true : chat.isSenderBlocked,
            isRecipientBlocked: chat.recipientId === payload.userId ? true : chat.isRecipientBlocked
          }
          : chat
      )
    }),

    [USER_UNBLOCK]: (state: chatInterface, { payload }) => ({
      ...state,
      chat: state.chat && (state.chat.senderId === payload.userId || state.chat.recipientId === payload.userId)
        ? {
          ...state.chat,
          isSenderBlocked: state.chat.senderId === payload.userId ? false : state.chat.isSenderBlocked,
          isRecipientBlocked: state.chat.recipientId === payload.userId ? false : state.chat.isRecipientBlocked
        }
        : state.chat,
      chats: state.chats.map(chat =>
        chat.senderId === payload.userId || chat.recipientId === payload.userId
          ? {
            ...chat,
            isSenderBlocked: chat.senderId === payload.userId ? false : chat.isSenderBlocked,
            isRecipientBlocked: chat.recipientId === payload.userId ? false : chat.isRecipientBlocked
          }
          : chat
      )
    }),

    [CHAT_DELETE]: (state: chatInterface, { payload }) => {
      const chatIds = payload && payload.chatIds ? payload.chatIds : [];
      return {
        ...state,
        chat: state.chat && chatIds.includes(state.chat.chatId) ? null : state.chat,
        chats: state.chats.filter(chat => !chatIds.includes(chat.chatId))
      };
    },
    [FILTER_INFO_GET]: (state: chatInterface, { payload }) => ({
      ...state,
      filterInfo: payload || null,
    }),
  },
  initialState
);


// WEBPACK FOOTER //
// ./src/routes/Chat/chat.ducks.ts